import { createSlice } from "@reduxjs/toolkit";
import { apiSlice } from "../api/apiSlice";
import { extractAccessTokenFromLoginPayload } from "../../utils/extractAccessToken";

// Helper function to safely parse JSON
const safeJSONParse = (data) => {
  try {
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return null;
  }
};

const sanitizeAuthString = (value) => {
  if (value == null) return null;

  const s = String(value).trim();

  if (!s || s === "undefined" || s === "null") return null;

  return s;
};

const accountFromProfilePayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const inner = payload.data;

  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    if (inner._id || inner.email || inner.first_name) return inner;

    const inner2 = inner.data;

    if (inner2 && typeof inner2 === "object" && !Array.isArray(inner2)) {
      return inner2;
    }
  }

  if (payload._id || payload.email) return payload;

  return null;
};

const initialState = {
  user: safeJSONParse(localStorage.getItem("user")),
  userData: safeJSONParse(localStorage.getItem("userData")),
  token: sanitizeAuthString(localStorage.getItem("token")),
  role: sanitizeAuthString(localStorage.getItem("role")),
  loading: false,
  initialLoading: true,
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, { payload: { user, token, role, userData } }) => {
      const safeToken = sanitizeAuthString(token);
      const safeRole = sanitizeAuthString(role);

      state.user = user;
      state.token = safeToken;
      state.role = safeRole;
      state.userData = userData;
      state.loading = false;
      state.initialLoading = false;
      state.error = null;

      localStorage.setItem("user", JSON.stringify(user));
      if (safeToken) localStorage.setItem("token", safeToken);
      else localStorage.removeItem("token");
      if (safeRole) localStorage.setItem("role", safeRole);
      else localStorage.removeItem("role");
      localStorage.setItem("userData", JSON.stringify(userData));
    },
    initializeAuth: (state) => {
      try {
        const token = sanitizeAuthString(localStorage.getItem("token"));
        const role = sanitizeAuthString(localStorage.getItem("role"));
        const user = safeJSONParse(localStorage.getItem("user"));
        const userData = safeJSONParse(localStorage.getItem("userData"));

        if (token && role && user) {
          state.token = token;
          state.role = role;
          state.user = user;
          state.userData = userData;
        }
      } catch (error) {
        // Clear all storage on error
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("userData");
        state.user = null;
        state.token = null;
        state.role = null;
        state.userData = null;
      }
      state.initialLoading = false;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.role = null;
      state.userData = null;
      state.loading = false;
      state.initialLoading = false;
      state.error = null;

      // Clear all storage
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("userData");
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(apiSlice.endpoints.login.matchPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(
        apiSlice.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          try {
            const account = payload?.data;
            const accessToken = extractAccessTokenFromLoginPayload(payload);
            const rawRole = account?.type ?? payload?.type;
            const role =
              rawRole != null && rawRole !== ""
                ? sanitizeAuthString(String(rawRole))
                : null;

            state.user = account;
            state.token = accessToken;
            state.role = role;

            state.userData = {
              id: account?._id,
              name: account?.full_name,
              email: account?.email,
              type: role,
              status: account?.status,
            };

            localStorage.setItem("user", JSON.stringify(account));
            if (accessToken) localStorage.setItem("token", accessToken);
            else localStorage.removeItem("token");
            if (role) localStorage.setItem("role", role);
            else localStorage.removeItem("role");
            localStorage.setItem("userData", JSON.stringify(state.userData));

            state.loading = false;
            state.initialLoading = false;
            state.error = null;
          } catch (error) {
            state.error = "Failed to store auth data";
          }
        },
      )
      .addMatcher(
        apiSlice.endpoints.login.matchRejected,
        (state, { error }) => {
          state.loading = false;
          state.initialLoading = false;
          state.error = error.message;
        },
      )
      .addMatcher(
        apiSlice.endpoints.getMyProfile.matchFulfilled,
        (state, { payload }) => {
          if (!state.token) return;

          const account = accountFromProfilePayload(payload);

          if (!account || typeof account !== "object") return;

          const prev =
            state.user && typeof state.user === "object"
              ? { ...state.user }
              : {};
          const mergedUser = {
            ...prev,
            ...account,
            data: {
              ...(typeof prev.data === "object" && prev.data ? prev.data : {}),
              ...account,
            },
          };

          state.user = mergedUser;

          const fullName =
            account.full_name ||
            [account.first_name, account.last_name]
              .filter(Boolean)
              .join(" ")
              .trim();

          state.userData = {
            ...state.userData,
            id: account._id ?? state.userData?.id,
            name: fullName || state.userData?.name,
            email: account.email ?? state.userData?.email,
            type: state.role ?? state.userData?.type,
            status: account.status ?? state.userData?.status,
            image: account.image ?? state.userData?.image,
          };

          localStorage.setItem("user", JSON.stringify(mergedUser));
          localStorage.setItem("userData", JSON.stringify(state.userData));
        },
      );
  },
});

export const {
  setCredentials,
  initializeAuth,
  startLoading,
  setError,
  logout,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state) => state.auth.user;
export const selectCurrentUserData = (state) => state.auth.userData;
export const selectCurrentToken = (state) => state.auth.token;
export const selectCurrentRole = (state) => state.auth.role;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
